import { zodResolver } from '@hookform/resolvers/zod';
import { formatMoney, Input, isDecimalAmount, Textarea } from '@platform/design-system';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormSheet } from '../FormSheet.tsx';
import { usePageContext } from '../usePageContext.ts';
import type { ResolveDisputeBody, Settlement } from './finance.ts';

const NOTE_MAX_LENGTH = 500;

const resolutionSchema = z.object({
  collectedAmount: z
    .string()
    .trim()
    .refine((value) => value === '' || isDecimalAmount(value)),
  note: z.string().trim().min(1).max(NOTE_MAX_LENGTH),
});

type ResolutionValues = z.infer<typeof resolutionSchema>;

export type ResolveDisputeSheetProps = {
  settlement: Settlement;
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onResolve: (body: ResolveDisputeBody) => void;
};

export const ResolveDisputeSheet = ({
  settlement,
  submitting,
  errorMessage,
  onClose,
  onResolve,
}: ResolveDisputeSheetProps) => {
  const { t, locale } = usePageContext();
  const form = useForm<ResolutionValues>({
    resolver: zodResolver(resolutionSchema),
    defaultValues: { collectedAmount: settlement.collectedAmount ?? '', note: '' },
    mode: 'onBlur',
  });
  const { errors } = form.formState;

  return (
    <FormSheet
      open
      formId="resolve-dispute"
      title={t('disputes.resolveTitle', { trackingNumber: settlement.trackingNumber })}
      submitLabel={t('disputes.resolve')}
      submitting={submitting}
      errorMessage={errorMessage}
      onClose={onClose}
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          onResolve({
            version: settlement.version,
            collectedAmount: values.collectedAmount.trim() === '' ? null : values.collectedAmount.trim(),
            note: values.note.trim(),
          });
        })(event);
      }}
    >
      <p>
        {t('disputes.expectedWas', {
          amount: formatMoney({ amount: settlement.expectedAmount, currency: settlement.currency }, locale),
        })}
      </p>
      <Input
        label={t('disputes.collectedAmount')}
        inputMode="decimal"
        suffix={settlement.currency}
        helperText={t('disputes.collectedHelp')}
        errorText={errors.collectedAmount ? t('disputes.amountInvalid') : undefined}
        {...form.register('collectedAmount')}
      />
      <Textarea
        label={t('disputes.note')}
        required
        rows={3}
        maxLength={NOTE_MAX_LENGTH}
        helperText={t('disputes.noteHelp')}
        errorText={errors.note ? t('disputes.noteRequired') : undefined}
        {...form.register('note')}
      />
    </FormSheet>
  );
};
